"use client";

import { useEffect, useRef, useState, type RefObject } from "react";
import { recordClientEvent } from "@/lib/client-observability";
import {
  createPaywallPriceWaitTracker,
  type PaywallPriceFailure,
  type PaywallPriceWaitTracker,
} from "@/lib/paywall-wait";

/**
 * Ожидание цен Paddle в блоке тарифов лендинга (`PricingV2`).
 *
 * t0 — первый кадр, где карточки тарифов попали в окно (IntersectionObserver),
 * а не монтирование: блок стоит внизу длинной страницы, цены запрашиваются при
 * загрузке, и большинство просмотров до тарифов не доходит. Отсчёт от загрузки
 * мерил бы скорость Paddle, а не то, сколько ждёт человек. Если к моменту
 * прокрутки цены уже есть, ожидание честно равно нулю.
 *
 * Ошибки Paddle (`paddle_init_error` / `paddle_price_preview_error`) тоже
 * уходят отсюда — в момент показа, по одной на показ.
 *
 * Один показ на монтирование блока: уход с экрана и возврат — тот же показ.
 * Трекер живёт в ref, поэтому повторный прогон эффектов в StrictMode не шлёт
 * событие второй раз, а cleanup только снимает таймер.
 */
export function usePaywallPriceWaitObservability(
  targetRef: RefObject<HTMLElement | null>,
  { enabled, pricesReady, failure }: { enabled: boolean; pricesReady: boolean; failure: PaywallPriceFailure | null },
) {
  const trackerRef = useRef<PaywallPriceWaitTracker | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled || visible) return undefined;
    const node = targetRef.current;
    if (!node) return undefined;

    const markVisible = () => {
      if (!trackerRef.current) trackerRef.current = createPaywallPriceWaitTracker(recordClientEvent);
      // t0 ставим здесь, в колбэке наблюдателя, а не в эффекте после
      // перерисовки — так время не включает лишний кадр.
      trackerRef.current.show();
      setVisible(true);
    };

    if (typeof IntersectionObserver === "undefined") {
      markVisible();
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      markVisible();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, visible, targetRef]);

  useEffect(() => {
    const tracker = trackerRef.current;
    if (!enabled || !visible || !tracker) return undefined;
    tracker.sync({ pricesReady, failure });
    return () => tracker.pause();
  }, [enabled, visible, pricesReady, failure]);
}
